import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Accordion } from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import {
  useCourse,
  useModules,
  useModuleContent,
  useUpdateCourse,
  useCreateModule,
  useUpdateModule,
  useCreateModuleContent,
  useUpdateModuleContent,
  useDeleteModuleContent,
  useDeleteModule,
  useReorderModules,
  useCloneModule,
  useToggleModulePublish,
} from "@/hooks/useCourses";
import { CompletionBadgeSelect } from "@/components/badges/CompletionBadgeSelect";
import { setContentBadge, useCanSetContentBadge, invalidateBadgeQueries } from "@/hooks/useBadges";
import { useHasRole } from "@/hooks/useUserRoles";
import { useAuth } from "@/lib/auth";
import { useQueryClient } from "@tanstack/react-query";
import { SurveyManager } from "@/components/lms/SurveyManager";
import { SurveyAnalyticsDashboard } from "@/components/lms/SurveyAnalyticsDashboard";
import { QuizManager } from "@/components/lms/QuizManager";
import { AssignmentManager } from "@/components/lms/AssignmentManager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SortableModuleItem } from "@/components/lms/SortableModuleItem";
import { ImagePicker } from "@/components/instructor/ImagePicker";
import {
  ArrowLeft,
  Check,
  FileImage,
  FileText,
  FileSpreadsheet,
  Link as LinkIcon,
  Music,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/errorLogger";

export default function CourseEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [editedTitle, setEditedTitle] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedThumbnail, setEditedThumbnail] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [newModuleDescription, setNewModuleDescription] = useState("");

  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [selectedModuleForContent, setSelectedModuleForContent] = useState<string | null>(null);
  const [newContentType, setNewContentType] = useState<"video" | "text" | "image" | "document" | "link" | "audio">("text");
  const [videoInputMode, setVideoInputMode] = useState<"embed" | "upload">("embed");
  const [newContentUrl, setNewContentUrl] = useState("");
  const [newContentText, setNewContentText] = useState("");
  const [uploadMode, setUploadMode] = useState<"url" | "file">("url");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: course, isLoading: courseLoading } = useCourse(id!);
  const { user: badgeUser } = useAuth();
  const isBadgeAdmin = useHasRole("admin");
  const badgeQueryClient = useQueryClient();
  const { data: canSetBadge } = useCanSetContentBadge("course", id, badgeUser?.id, isBadgeAdmin);
  const [editedBadgeId, setEditedBadgeId] = useState<string | null | undefined>(undefined);
  const { data: modules, isLoading: modulesLoading } = useModules(id!);

  const updateCourseMutation = useUpdateCourse();
  const createModuleMutation = useCreateModule();
  const updateModuleMutation = useUpdateModule();
  const createContentMutation = useCreateModuleContent();
  const deleteModuleMutation = useDeleteModule();
  const reorderModulesMutation = useReorderModules();
  const cloneModuleMutation = useCloneModule();
  const togglePublishMutation = useToggleModulePublish();

  const handleUpdateModule = async (moduleId: string, title: string, description: string, thumbnail_url?: string | null, requiresHomework?: boolean) => {
    try {
      await updateModuleMutation.mutateAsync({
        id: moduleId,
        courseId: id!,
        title,
        description: description || undefined,
        thumbnail_url,
        ...(requiresHomework === undefined ? {} : { requires_homework: requiresHomework }),
      });
      toast({ title: "Module updated" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update module",
        variant: "destructive",
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && modules) {
      const oldIndex = modules.findIndex((m) => m.id === active.id);
      const newIndex = modules.findIndex((m) => m.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new order
        const reorderedModules = [...modules];
        const [movedModule] = reorderedModules.splice(oldIndex, 1);
        reorderedModules.splice(newIndex, 0, movedModule);

        // Build update payload with new sequence_order values
        const updates = reorderedModules.map((m, idx) => ({
          id: m.id,
          sequence_order: idx + 1,
        }));

        try {
          await reorderModulesMutation.mutateAsync({
            courseId: id!,
            modules: updates,
          });
          toast({ title: "Module order updated" });
        } catch (error: any) {
          console.error("Reorder error:", error);
          toast({
            title: "Error",
            description: error?.message || "Failed to reorder modules.",
            variant: "destructive",
          });
        }
      }
    }
  };

  // Initialize form with course data when it loads
  useEffect(() => {
    if (course) {
      setEditedTitle(course.title);
      setEditedDescription(course.description || "");
      setEditedThumbnail(course.thumbnail_url || null);
    }
  }, [course]);

  const handleSaveCourse = async () => {
    try {
      await updateCourseMutation.mutateAsync({
        id: id!,
        title: editedTitle,
        description: editedDescription,
        thumbnail_url: editedThumbnail,
      });
      const currentBadge = (course as { badge_id?: string | null } | undefined)?.badge_id ?? null;
      if (canSetBadge && editedBadgeId !== undefined && editedBadgeId !== currentBadge) {
        await setContentBadge("course", id!, editedBadgeId);
        invalidateBadgeQueries(badgeQueryClient);
        badgeQueryClient.invalidateQueries({ queryKey: ["course", id] });
      }
      setHasChanges(false);
      toast({ title: "Course saved!" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save course.",
        variant: "destructive",
      });
    }
  };

  const handleAddModule = async () => {
    if (!newModuleTitle.trim()) return;

    try {
      await createModuleMutation.mutateAsync({
        course_id: id!,
        title: newModuleTitle,
        description: newModuleDescription || undefined,
        sequence_order: (modules?.length || 0) + 1,
      });
      toast({ title: "Module added!" });
      setModuleDialogOpen(false);
      setNewModuleTitle("");
      setNewModuleDescription("");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add module.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteModule = async (moduleId: string) => {
    try {
      await deleteModuleMutation.mutateAsync({ moduleId, courseId: id! });
      toast({ title: "Module deleted" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete module.",
        variant: "destructive",
      });
    }
  };

  const handleCloneModule = async (moduleId: string) => {
    try {
      await cloneModuleMutation.mutateAsync({ moduleId, courseId: id! });
      toast({ title: "Module cloned!" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clone module.",
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type for document uploads
      if (newContentType === "document") {
        const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'];
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        if (!fileExt || !allowedExtensions.includes(fileExt)) {
          toast({
            title: "Invalid file type",
            description: "Only PDF, Word, Excel, JPG, and PNG files are allowed.",
            variant: "destructive",
          });
          e.target.value = '';
          return;
        }
      }
      setSelectedFile(file);
    }
  };

  /**
   * Uploads a file and returns the storage PATH (not full URL)
   */
  const uploadFile = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${crypto.randomUUID()}.${fileExt}`;
    const filePath = `${selectedModuleForContent}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('course-content')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Return the path, not the full URL
    return filePath;
  };

  const handleAddContent = async () => {
    if (!selectedModuleForContent) return;

    try {
      setIsUploading(true);
      let contentUrl = newContentUrl;

      // Upload file if in file mode (for image, document, video upload, or audio)
      const needsUpload = 
        (newContentType === "image" && uploadMode === "file") ||
        (newContentType === "document") ||
        (newContentType === "video" && videoInputMode === "upload") ||
        (newContentType === "audio");
      
      if (needsUpload && selectedFile) {
        try {
          contentUrl = await uploadFile(selectedFile);
        } catch (uploadError: any) {
          console.error("File upload error:", uploadError);
          await logError({
            error: uploadError,
            component: "CourseEditor",
            action: "uploadFile",
            metadata: { 
              contentType: newContentType, 
              fileName: selectedFile?.name,
              fileSize: selectedFile?.size,
            },
          });
          toast({
            title: "Upload Failed",
            description: uploadError?.message || "Failed to upload file. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Extract display title from filename (without extension)
      let displayTitle: string | undefined;
      if (selectedFile) {
        const fileName = selectedFile.name;
        displayTitle = fileName.replace(/\.[^/.]+$/, ""); // Remove extension
      }

      await createContentMutation.mutateAsync({
        module_id: selectedModuleForContent,
        content_type: newContentType,
        content_url: newContentType !== "text" ? contentUrl : undefined,
        content_text: newContentType === "text" ? newContentText : undefined,
        display_title: displayTitle,
        sequence_order: 1,
      });
      toast({ title: "Content added!" });
      setContentDialogOpen(false);
      setNewContentType("text");
      setNewContentUrl("");
      setNewContentText("");
      setSelectedModuleForContent(null);
      setUploadMode("url");
      setVideoInputMode("embed");
      setSelectedFile(null);
    } catch (error: any) {
      console.error("Content creation error:", error);
      await logError({
        error: error,
        component: "CourseEditor",
        action: "createContent",
        metadata: { 
          contentType: newContentType,
          moduleId: selectedModuleForContent,
        },
      });
      toast({
        title: "Error",
        description: error?.message || "Failed to add content.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (courseLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold">Course not found</h2>
          <Button
            variant="link"
            onClick={() => navigate("/instructor/courses")}
          >
            Back to courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/instructor/courses")}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to courses
            </Button>
            <h1 className="text-2xl font-bold">Edit Course</h1>
          </div>

          <Button
            onClick={handleSaveCourse}
            disabled={updateCourseMutation.isPending}
          >
            <Save className="h-4 w-4 mr-2" />
            {updateCourseMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        {/* Course details */}
        <Card>
          <CardHeader>
            <CardTitle>Course Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={editedTitle || course.title}
                onChange={(e) => {
                  setEditedTitle(e.target.value);
                  setHasChanges(true);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={editedDescription || course.description || ""}
                onChange={(e) => {
                  setEditedDescription(e.target.value);
                  setHasChanges(true);
                }}
                rows={3}
              />
            </div>
            <ImagePicker
              value={editedThumbnail || course.thumbnail_url}
              onChange={(url) => {
                setEditedThumbnail(url);
                setHasChanges(true);
              }}
              label="Course Featured Image"
            />
            {canSetBadge && (
              <CompletionBadgeSelect
                value={editedBadgeId !== undefined ? editedBadgeId : ((course as { badge_id?: string | null }).badge_id ?? null)}
                onChange={(v) => {
                  setEditedBadgeId(v);
                  setHasChanges(true);
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Modules */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Modules</CardTitle>
            <Dialog open={moduleDialogOpen} onOpenChange={setModuleDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add Module
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Module</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Module Title</Label>
                    <Input
                      placeholder="Enter module title"
                      value={newModuleTitle}
                      onChange={(e) => setNewModuleTitle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (optional)</Label>
                    <Textarea
                      placeholder="Module description"
                      value={newModuleDescription}
                      onChange={(e) => setNewModuleDescription(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <Button
                    onClick={handleAddModule}
                    disabled={!newModuleTitle.trim() || createModuleMutation.isPending}
                    className="w-full"
                  >
                    {createModuleMutation.isPending ? "Adding..." : "Add Module"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {modulesLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : modules && modules.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={modules.map((m) => m.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Accordion type="single" collapsible className="w-full">
                    {modules.map((module, index) => (
                      <SortableModuleItem
                        key={module.id}
                        module={module}
                        index={index}
                        onDelete={handleDeleteModule}
                        onUpdate={handleUpdateModule}
                        onClone={handleCloneModule}
                        onTogglePublish={(moduleId, isPublished) => {
                          togglePublishMutation.mutate({ moduleId, courseId: id!, isPublished });
                        }}
                        onChangeStatus={(moduleId, next) => {
                          updateModuleMutation.mutate({ id: moduleId, courseId: id!, status: next } as any);
                        }}
                        isDeleting={deleteModuleMutation.isPending}
                        isUpdating={updateModuleMutation.isPending}
                        isCloning={cloneModuleMutation.isPending}
                      >
                        <Tabs defaultValue="content" className="w-full">
                          <TabsList className="mb-4">
                            <TabsTrigger value="content">Content</TabsTrigger>
                            <TabsTrigger value="assignments">Assignments</TabsTrigger>
                            <TabsTrigger value="quizzes">Quizzes</TabsTrigger>
                          </TabsList>
                          <TabsContent value="content" className="space-y-4">
                            <ModuleContentList moduleId={module.id} />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedModuleForContent(module.id);
                                setContentDialogOpen(true);
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" /> Add Content
                            </Button>
                          </TabsContent>
                          <TabsContent value="assignments">
                            <AssignmentManager moduleId={module.id} />
                          </TabsContent>
                          <TabsContent value="quizzes">
                            <QuizManager moduleId={module.id} moduleName={module.title} />
                          </TabsContent>
                        </Tabs>
                      </SortableModuleItem>
                    ))}
                  </Accordion>
                </SortableContext>
              </DndContext>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No modules yet. Add your first module to get started.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Surveys */}
        <Card>
          <CardContent className="pt-6">
            <SurveyManager courseId={id!} />
          </CardContent>
        </Card>

        {/* Survey Analytics */}
        <Card>
          <CardContent className="pt-6">
            <SurveyAnalyticsDashboard courseId={id!} />
          </CardContent>
        </Card>

        {/* Add Content Dialog */}
        <Dialog open={contentDialogOpen} onOpenChange={setContentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Content</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select
                  value={newContentType}
                  onValueChange={(v) => {
                    setNewContentType(v as "video" | "text" | "image" | "document" | "link" | "audio");
                    setSelectedFile(null);
                    setNewContentUrl("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" /> Text
                      </div>
                    </SelectItem>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4" /> Video
                      </div>
                    </SelectItem>
                    <SelectItem value="image">
                      <div className="flex items-center gap-2">
                        <FileImage className="h-4 w-4" /> Image
                      </div>
                    </SelectItem>
                    <SelectItem value="document">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" /> Document
                      </div>
                    </SelectItem>
                    <SelectItem value="link">
                      <div className="flex items-center gap-2">
                        <LinkIcon className="h-4 w-4" /> Link
                      </div>
                    </SelectItem>
                    <SelectItem value="audio">
                      <div className="flex items-center gap-2">
                        <Music className="h-4 w-4" /> Audio
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newContentType === "text" && (
                <div className="space-y-2">
                  <Label>Content</Label>
                  <Textarea
                    placeholder="Enter your text content..."
                    value={newContentText}
                    onChange={(e) => setNewContentText(e.target.value)}
                    rows={6}
                  />
                </div>
              )}

              {newContentType === "video" && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={videoInputMode === "embed" ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setVideoInputMode("embed");
                        setSelectedFile(null);
                      }}
                    >
                      Embed Link
                    </Button>
                    <Button
                      type="button"
                      variant={videoInputMode === "upload" ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setVideoInputMode("upload");
                        setNewContentUrl("");
                      }}
                    >
                      <Upload className="h-4 w-4 mr-1" /> Upload
                    </Button>
                  </div>

                  {videoInputMode === "embed" ? (
                    <div className="space-y-2">
                      <Label>Video URL</Label>
                      <Input
                        placeholder="YouTube or Vimeo share link (e.g., https://youtu.be/... or https://vimeo.com/...)"
                        value={newContentUrl}
                        onChange={(e) => setNewContentUrl(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Paste a YouTube or Vimeo share link to embed the video
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Upload Video</Label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
                      >
                        {selectedFile ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              Click to select a video file
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {newContentType === "image" && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={uploadMode === "url" ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setUploadMode("url");
                        setSelectedFile(null);
                      }}
                    >
                      URL
                    </Button>
                    <Button
                      type="button"
                      variant={uploadMode === "file" ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setUploadMode("file");
                        setNewContentUrl("");
                      }}
                    >
                      <Upload className="h-4 w-4 mr-1" /> Upload
                    </Button>
                  </div>

                  {uploadMode === "url" ? (
                    <div className="space-y-2">
                      <Label>Image URL</Label>
                      <Input
                        placeholder="Enter image URL"
                        value={newContentUrl}
                        onChange={(e) => setNewContentUrl(e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Upload Image</Label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
                      >
                        {selectedFile ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              Click to select an image
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {newContentType === "document" && (
                <div className="space-y-2">
                  <Label>Upload Document</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
                  >
                    {selectedFile ? (
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Click to select a document
                        </p>
                        <p className="text-xs text-muted-foreground">
                          PDF, Word, Excel, JPG, or PNG files only
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {newContentType === "link" && (
                <div className="space-y-2">
                  <Label>Web Resource URL</Label>
                  <Input
                    placeholder="https://example.com/resource"
                    value={newContentUrl}
                    onChange={(e) => setNewContentUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter a link to an external web resource
                  </p>
                </div>
              )}

              {newContentType === "audio" && (
                <div className="space-y-2">
                  <Label>Upload Audio</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/mp3,audio/mpeg,.mp3"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
                  >
                    {selectedFile ? (
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Music className="h-8 w-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Click to select an audio file
                        </p>
                        <p className="text-xs text-muted-foreground">
                          MP3 files only
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                onClick={handleAddContent}
                disabled={
                  isUploading ||
                  createContentMutation.isPending ||
                  (newContentType === "text" && !newContentText.trim()) ||
                  (newContentType === "video" && videoInputMode === "embed" && !newContentUrl.trim()) ||
                  (newContentType === "video" && videoInputMode === "upload" && !selectedFile) ||
                  (newContentType === "image" && uploadMode === "url" && !newContentUrl.trim()) ||
                  (newContentType === "image" && uploadMode === "file" && !selectedFile) ||
                  (newContentType === "document" && !selectedFile) ||
                  (newContentType === "link" && !newContentUrl.trim()) ||
                  (newContentType === "audio" && !selectedFile)
                }
                className="w-full"
              >
                {isUploading ? "Uploading..." : createContentMutation.isPending ? "Adding..." : "Add Content"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function ModuleContentList({ moduleId }: { moduleId: string }) {
  const { data: content, isLoading } = useModuleContent(moduleId);
  const deleteContentMutation = useDeleteModuleContent();
  const updateContentMutation = useUpdateModuleContent();
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editingContentType, setEditingContentType] = useState<string | null>(null);

  const handleDelete = async (contentId: string) => {
    try {
      await deleteContentMutation.mutateAsync({ contentId, moduleId });
      toast({ title: "Content deleted" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete content.",
        variant: "destructive",
      });
    }
  };

  const handleStartEdit = (item: { id: string; display_title: string | null; content_type: string; content_url: string | null }) => {
    setEditingId(item.id);
    setEditTitle(item.display_title || item.content_type);
    setEditUrl(item.content_url || "");
    setEditingContentType(item.content_type);
  };

  const handleSaveEdit = async (itemId: string) => {
    try {
      const updatePayload: { id: string; moduleId: string; display_title?: string; content_url?: string } = {
        id: itemId,
        moduleId,
        display_title: editTitle.trim() || null,
      };
      
      // Only include content_url for videos with external URLs (not storage paths)
      if (editingContentType === "video" && editUrl && !editUrl.startsWith(moduleId)) {
        updatePayload.content_url = editUrl.trim();
      }
      
      await updateContentMutation.mutateAsync(updatePayload);
      toast({ title: "Content updated" });
      setEditingId(null);
      setEditUrl("");
      setEditingContentType(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update content.",
        variant: "destructive",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditUrl("");
    setEditingContentType(null);
  };
  
  // Check if a URL is a storage path (uploaded file) vs external URL
  const isStoragePath = (url: string | null) => {
    if (!url) return false;
    return !url.startsWith("http");
  };

  if (isLoading) {
    return <Skeleton className="h-8" />;
  }

  if (!content || content.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">No content yet</p>
    );
  }

  const getContentLabel = (item: { content_type: string; display_title: string | null }) => {
    if (item.display_title) return item.display_title;
    return item.content_type.charAt(0).toUpperCase() + item.content_type.slice(1);
  };

  return (
    <div className="space-y-2">
      {content.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between gap-2 p-2 bg-muted rounded text-sm"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {item.content_type === "video" && <Video className="h-4 w-4 shrink-0" />}
            {item.content_type === "text" && <FileText className="h-4 w-4 shrink-0" />}
            {item.content_type === "image" && <FileImage className="h-4 w-4 shrink-0" />}
            {item.content_type === "document" && <FileSpreadsheet className="h-4 w-4 shrink-0" />}
            {item.content_type === "link" && <LinkIcon className="h-4 w-4 shrink-0" />}
            {editingId === item.id ? (
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-1">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground mb-1 block">Title</Label>
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="h-7 text-sm"
                      autoFocus
                      placeholder="Enter title"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                    />
                  </div>
                </div>
                {item.content_type === "video" && !isStoragePath(item.content_url) && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Video URL</Label>
                    <Input
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      className="h-7 text-sm"
                      placeholder="YouTube, Vimeo, or direct video URL"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                    />
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSaveEdit(item.id)}
                    disabled={updateContentMutation.isPending}
                    className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelEdit}
                    className="h-7 px-2"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <span className="truncate">{getContentLabel(item)}</span>
            )}
          </div>
          {editingId !== item.id && (
            <div className="flex items-center gap-1 shrink-0">
              {(item.content_type === "document" || item.content_type === "image" || item.content_type === "video") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStartEdit(item)}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(item.id)}
                disabled={deleteContentMutation.isPending}
                className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
